<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumb" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Control Info -->
            <div class="row mb-3" v-if="control.id">
              <div class="col-sm-12">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 class="mb-1">{{ control.name }}</h5>
                    <span class="text-muted">Identificador: {{ control.identifier_label }} | Modo: {{ modeLabel }}</span>
                  </div>
                  <div>
                    <button v-if="hasGroup" type="button" class="btn btn-outline-info mr-2" @click="openGroups()">
                      <i class="bx bx-group mr-1"></i> {{ canManage ? 'Gerenciar Grupos' : 'Ver Grupos' }}
                    </button>
                    <button v-if="canManage" type="button" class="btn btn-outline-primary mr-2" @click="openImport()">
                      <i class="bx bx-upload mr-1"></i> Importar Planilha
                    </button>
                    <button v-if="canManage" type="button" class="btn btn-success btn-rounded" @click="newItem()">
                      <i class="mdi mdi-plus mr-1"></i> Novo Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <!-- Search -->
            <div class="row mb-2 align-items-center">
              <div class="col">
                <div class="search-box">
                  <div class="position-relative">
                    <input type="text" class="form-control" v-model="search" placeholder="Buscar por nome, identificador, grupo..." @keyup.enter="doSearch"/>
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-auto d-flex" style="gap: 8px;">
                <button v-if="vazioFilterActive && canManage" type="button" class="btn" :class="selectionMode ? 'btn-primary' : 'btn-outline-primary'" @click="toggleSelectionMode()">
                  <i class="bx bx-user-plus mr-1"></i> Atribuir a grupo
                </button>
                <button type="button" class="btn" :class="hasActiveFilters ? 'btn-warning' : 'btn-outline-secondary'" @click="openFilters()">
                  <i class="bx bx-filter-alt mr-1"></i> Filtros
                  <span v-if="activeFilterCount > 0" class="badge badge-light ml-1">{{ activeFilterCount }}</span>
                </button>
                <button type="button" class="btn btn-outline-success" @click="doExport()" :disabled="exporting">
                  <i class="bx" :class="exporting ? 'bx-loader-alt bx-spin' : 'bx-download'" style="margin-right: 4px;"></i>
                  Exportar
                </button>
              </div>
            </div>
            <!-- Barra de atribuição em massa -->
            <div v-if="selectionMode" class="alert alert-info d-flex flex-wrap align-items-center mb-2" style="gap: 10px;">
              <strong>{{ selectedItems.length }} selecionado(s)</strong>
              <select class="form-control form-control-sm" style="width: auto;" v-model="assignGroupId">
                <option value="">Selecione o grupo…</option>
                <option v-for="g in groupsList" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
              <button type="button" class="btn btn-sm btn-primary" :disabled="!assignGroupId || selectedItems.length === 0 || assigning" @click="bulkAssign(false)">
                <i class="bx bx-check mr-1"></i> Aplicar aos selecionados ({{ selectedItems.length }})
              </button>
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!assignGroupId || assigning || rows === 0" @click="bulkAssign(true)">
                Aplicar a todos do filtro ({{ rows }})
              </button>
              <button type="button" class="btn btn-sm btn-secondary ml-auto" :disabled="assigning" @click="cancelSelection()">Cancelar</button>
            </div>
            <!-- Table -->
            <div class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th v-if="selectionMode" width="3%">
                      <input type="checkbox" @change="toggleSelectAll()" :checked="allPageSelected" />
                    </th>
                    <th>{{ control.identifier_label || 'Identificador' }}</th>
                    <th>Nome</th>
                    <th v-if="hasGroup">Grupo</th>
                    <th v-if="control.assign_participant_group">Grupo do Participante</th>
                    <th v-if="hasTagBundles">Grupo de Tags</th>
                    <th v-if="showItemTagsColumn">Tags</th>
                    <th v-for="field in (control.fields_schema || [])" :key="field.name">{{ field.label }}</th>
                    <template v-if="isCombinedMode">
                      <th>Limite</th>
                      <th>Limite Grupo</th>
                      <th>Usados Item</th>
                      <th>Usados Grupo</th>
                    </template>
                    <template v-else>
                      <th>{{ limitHeaderLabel }}</th>
                      <th>Usados</th>
                    </template>
                    <th>Status</th>
                    <th v-if="canManage">#</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in itemsList" :key="item.id" :class="{ 'table-active': selectionMode && isSelected(item.id) }">
                    <td v-if="selectionMode" @click.stop="toggleSelection(item.id)">
                      <input type="checkbox" :checked="isSelected(item.id)" @click.stop="toggleSelection(item.id)" />
                    </td>
                    <td class="link" @click="editItem(item)">
                      {{ item.identifier }}
                    </td>
                    <td class="link" @click="editItem(item)">
                      <span class="badge font-size-14 badge-soft-primary">{{ item.name }}</span>
                    </td>
                    <td v-if="hasGroup" class="link" @click="editItem(item)">
                      {{ item.group_name || '-' }}
                      <i v-if="item.group_name" class="bx bx-copy copy-icon ml-1" @click.stop="copyText(item.group_name)"></i>
                    </td>
                    <td v-if="control.assign_participant_group" class="link" @click="editItem(item)">
                      <span v-if="item.participant_group_name" class="badge badge-soft-info font-size-12">{{ item.participant_group_name }}</span>
                      <span v-else>-</span>
                    </td>
                    <td v-if="hasTagBundles" class="link" @click="editItem(item)">
                      <span v-if="item.tag_bundle_name" class="badge badge-soft-primary font-size-12">{{ item.tag_bundle_name }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td v-if="showItemTagsColumn" class="link" @click="editItem(item)">
                      <template v-if="item.tags && item.tags.length">
                        <span v-for="t in item.tags" :key="t.id" class="badge badge-soft-success font-size-12 mr-1">{{ t.name }}</span>
                      </template>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td v-for="field in (control.fields_schema || [])" :key="field.name" class="link" @click="editItem(item)">
                      {{ formatFieldValue(item.data, field) }}
                    </td>
                    <template v-if="isCombinedMode">
                      <td class="link" @click="editItem(item)">{{ item.limit_register || '∞' }}</td>
                      <td class="link" @click="editItem(item)">{{ item.group_limit || '∞' }}</td>
                      <td class="link" @click="editItem(item)">
                        <span class="badge font-size-12" :class="makeUsedBadgeClass(item.used_count, item.limit_register)">{{ makeUsedLabel(item.used_count, item.limit_register) }}</span>
                      </td>
                      <td class="link" @click="editItem(item)">
                        <span class="badge font-size-12" :class="makeUsedBadgeClass(item.group_used_count, item.group_limit)">{{ makeUsedLabel(item.group_used_count, item.group_limit) }}</span>
                      </td>
                    </template>
                    <template v-else>
                      <td class="link" @click="editItem(item)">
                        {{ getItemLimit(item) }}
                      </td>
                      <td class="link" @click="editItem(item)">
                        <span class="badge font-size-12" :class="getUsedBadgeClass(item)">{{ getUsedLabel(item) }}</span>
                      </td>
                    </template>
                    <td class="link" @click="editItem(item)">
                      <span class="badge font-size-12" :class="{'badge-soft-success': item.active, 'badge-soft-danger': !item.active}">
                        {{ item.active ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td v-if="canManage">
                      <button type="button" @click="deleteItem(item)" class="link btn btn-outline-danger btn-sm">
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" @input="updateList"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <item-form v-model="modalItemForm" :controlData="control" :itemData="selectedItem" :groupsList="groupsList" :participantGroupsList="participantGroupsList" :canManage="canManage" ref="formItem" @created="updateList"></item-form>
    <groups-modal v-model="modalGroups" :controlId="controlId" :canManage="canManage" ref="groupsModal"></groups-modal>
    <import-items :controlId="controlId" :controlData="control" :groupsList="groupsList" :participantGroupsList="participantGroupsList" ref="importItems" @imported="updateList"></import-items>

    <!-- Filter Modal -->
    <b-modal v-model="showFilterModal" title="Filtros" size="md" hide-header-close>
      <div class="form-group" v-if="hasGroup">
        <label>Grupo</label>
        <select class="form-control" v-model="filters.group_id">
          <option :value="undefined">-- Todos --</option>
          <option value="__none__">Vazio (sem grupo)</option>
          <option v-for="g in groupsList" :key="g.id" :value="g.id">{{ g.name }}</option>
        </select>
      </div>
      <div class="form-group" v-if="control.assign_participant_group">
        <label>Grupo do Participante</label>
        <select class="form-control" v-model="filters.participant_group_id">
          <option :value="undefined">-- Todos --</option>
          <option v-for="pg in participantGroupsList" :key="pg.id" :value="pg.id">{{ pg.name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select class="form-control" v-model="filters.active">
          <option :value="undefined">-- Todos --</option>
          <option :value="1">Ativo</option>
          <option :value="0">Inativo</option>
        </select>
      </div>
      <hr v-if="control.fields_schema && control.fields_schema.length" />
      <div v-for="field in (control.fields_schema || [])" :key="field.name" class="form-group">
        <label>{{ field.label }}</label>
        <select v-if="field.type === 'select'" class="form-control" v-model="filters[field.name]">
          <option :value="undefined">-- Todos --</option>
          <option v-for="opt in field.options" :key="opt.value || opt" :value="opt.value || opt">{{ opt.label || opt }}</option>
        </select>
        <multiselect
          v-else-if="field.type === 'multiselect'"
          v-model="filters[field.name]"
          :options="(field.options || []).map(o => o.value || o)"
          :multiple="true"
          :close-on-select="false"
          placeholder="Selecione..."
        ></multiselect>
        <input v-else :type="field.type === 'number' ? 'number' : 'text'" class="form-control" v-model="filters[field.name]" :placeholder="field.label" />
      </div>
      <template #modal-footer>
        <button type="button" class="btn btn-secondary" @click="showFilterModal = false">Fechar</button>
        <button type="button" class="btn btn-outline-danger" @click="clearFilters()">Limpar</button>
        <button type="button" class="btn btn-primary" @click="applyFilters()">Aplicar</button>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import ItemForm from './ItemForm'
import GroupsModal from './Groups'
import ImportItems from './ImportItems'
import Multiselect from 'vue-multiselect'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState, mapGetters } from 'vuex'
import { list as listParticipantGroups } from '@/pages/groups/services'
import { exportItems, bulkAssignItemsGroup } from './services'

export default {
  components: {
    Layout,
    PageHeader,
    ItemForm,
    GroupsModal,
    ImportItems,
    Multiselect
  },
  name: 'RegistrationControlItems',
  page: {
    title: 'Itens do Controle',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      search: '',
      modalItemForm: false,
      modalGroups: false,
      showFilterModal: false,
      filters: {},
      params: {},
      currentPage: 1,
      perPage: 20,
      selectedItem: null,
      exporting: false,
      selectionMode: false,
      selectedItems: [],
      assignGroupId: '',
      assigning: false,
      groupsList: [],
      participantGroupsList: [],
      title: 'Itens do Controle',
      breadcrumb: [{
        text: 'Controle de Inscrição',
        href: '/registration-controls'
      },
      {
        text: 'Itens',
        active: true
      }]
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['hasPermission']),
    canManage () {
      return this.hasPermission('registration_controls.manage')
    },
    ...mapState('registrationControl', {
      control: 'control',
      itemsList: 'items',
      itemsPagination: 'itemsPagination'
    }),
    controlId () {
      return this.$route.params.controlId
    },
    rows () {
      return this.itemsPagination ? this.itemsPagination.total : 0
    },
    hasActiveFilters () {
      return Object.values(this.filters).some(v => v !== undefined && v !== '' && v !== null && !(Array.isArray(v) && v.length === 0))
    },
    activeFilterCount () {
      return Object.values(this.filters).filter(v => v !== undefined && v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)).length
    },
    hasTagBundles () {
      return Array.isArray(this.control.tag_bundles) && this.control.tag_bundles.length > 0
    },
    // Mostra a coluna "Tags" se algum item da página atual tiver tags diretas
    showItemTagsColumn () {
      return Array.isArray(this.itemsList) && this.itemsList.some(i => Array.isArray(i.tags) && i.tags.length > 0)
    },
    hasGroup () {
      return ['per_group', 'per_item_and_group'].includes(this.control.vacancy_mode)
    },
    // Atribuição em massa só aparece quando o filtro "Vazio (sem grupo)" está APLICADO
    vazioFilterActive () {
      return this.params.group_id === '__none__'
    },
    allPageSelected () {
      return this.itemsList.length > 0 && this.selectedItems.length === this.itemsList.length
    },
    isCombinedMode () {
      return this.control.vacancy_mode === 'per_item_and_group'
    },
    modeLabel () {
      if (this.control.vacancy_mode === 'per_item') return 'Individual'
      if (this.control.vacancy_mode === 'per_group') return 'Em grupo'
      if (this.control.vacancy_mode === 'per_item_and_group') return 'Individual + Grupo'
      return this.control.vacancy_mode
    },
    limitHeaderLabel () {
      if (this.control.vacancy_mode === 'per_group') return 'Limite Grupo'
      if (this.control.vacancy_mode === 'per_item_and_group') return 'Limite (Item/Grupo)'
      return 'Limite'
    }
  },
  mounted () {
    this.loadControl()
    this.updateList()
    this.loadGroups()
    this.loadParticipantGroups()
  },
  methods: {
    ...mapActions('registrationControl', {
      showControl: 'show',
      getItems: 'getItems',
      deleteItemAction: 'removeItem',
      getGroupsAction: 'getGroups'
    }),
    loadControl () {
      this.showControl(this.controlId).then(() => {
        this.title = 'Itens: ' + this.control.name
      })
    },
    loadGroups () {
      this.getGroupsAction({ controlId: this.controlId, params: { limit: 100 } }).then(() => {
        this.groupsList = this.$store.state.registrationControl.groups
      })
    },
    async loadParticipantGroups () {
      try {
        const res = await listParticipantGroups({ limit: 100 })
        this.participantGroupsList = res.data.data || res.data || []
      } catch (e) {
        this.participantGroupsList = []
      }
    },
    newItem () {
      this.selectedItem = null
      this.$refs.formItem.open(null)
      this.modalItemForm = true
    },
    editItem (item) {
      this.selectedItem = item
      this.$refs.formItem.open(item)
      this.modalItemForm = true
    },
    openGroups () {
      this.$refs.groupsModal.open()
      this.modalGroups = true
    },
    openImport () {
      this.$refs.importItems.open()
    },
    deleteItem (item) {
      Swal.fire({
        title: 'Você tem certeza ?',
        html: `Será excluído o item: <b>${item.name}</b>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(result => {
        if (result.value) {
          try {
            this.deleteItemAction({ controlId: this.controlId, id: item.id })
            successMessage('Sucesso: Item excluído com sucesso.', 2500)
            this.updateList()
          } catch (err) {
            errorMessage('Alerta: ' + err.response.data.message)
          }
        }
      })
    },
    formatFieldValue (data, field) {
      const val = data ? data[field.name] : null
      if (!val) return '-'
      if (Array.isArray(val)) return val.join(', ')
      return val
    },
    getItemLimit (item) {
      if (this.control.vacancy_mode === 'per_item_and_group') {
        const itemLim = item.limit_register || '∞'
        const groupLim = item.group_limit || '∞'
        return `${itemLim} / ${groupLim}`
      }
      if (this.control.vacancy_mode === 'per_group') {
        return item.group_limit || '∞'
      }
      return item.limit_register || '∞'
    },
    getEffectiveLimit (item) {
      if (this.control.vacancy_mode === 'per_item_and_group') {
        const itemLim = item.limit_register
        const groupLim = item.group_limit
        if (itemLim && groupLim) return Math.min(itemLim, groupLim)
        return itemLim || groupLim || 0
      }
      if (this.control.vacancy_mode === 'per_group') {
        return item.group_limit || 0
      }
      return item.limit_register || 0
    },
    getUsedLabel (item) {
      const limit = this.getEffectiveLimit(item)
      if (this.hasGroup) {
        const used = item.group_used_count ?? item.used_count
        return limit ? `${used}/${limit}` : `${used}/∞`
      }
      return limit ? `${item.used_count}/${limit}` : `${item.used_count}/∞`
    },
    getUsedBadgeClass (item) {
      const limit = this.getEffectiveLimit(item)
      const used = this.hasGroup ? (item.group_used_count ?? item.used_count) : item.used_count
      if (limit && used >= limit) {
        return 'badge-soft-danger'
      }
      if (used > 0) {
        return 'badge-soft-warning'
      }
      return 'badge-soft-success'
    },
    makeUsedLabel (used, limit) {
      const u = used ?? 0
      return limit ? `${u}/${limit}` : `${u}/∞`
    },
    makeUsedBadgeClass (used, limit) {
      const u = used ?? 0
      if (limit && u >= limit) return 'badge-soft-danger'
      if (u > 0) return 'badge-soft-warning'
      return 'badge-soft-success'
    },
    openFilters () {
      this.showFilterModal = true
    },
    applyFilters () {
      this.currentPage = 1
      this.showFilterModal = false
      this.getReport()
    },
    clearFilters () {
      this.filters = {}
      this.currentPage = 1
      this.showFilterModal = false
      this.getReport()
    },
    copyText (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success('Copiado!')
      })
    },
    getReport () {
      let params = {
        search: this.search || null,
        limit: this.perPage,
        page: this.currentPage
      }
      // Merge fixed filters
      if (this.filters.group_id !== undefined && this.filters.group_id !== '') {
        params.group_id = this.filters.group_id
      }
      if (this.filters.active !== undefined && this.filters.active !== '') {
        params.active = this.filters.active
      }
      if (this.filters.participant_group_id !== undefined && this.filters.participant_group_id !== '') {
        params.participant_group_id = this.filters.participant_group_id
      }
      // Merge dynamic field filters
      const dynamicFilters = {}
      Object.keys(this.filters).forEach(key => {
        if (key === 'group_id' || key === 'active' || key === 'participant_group_id') return
        const val = this.filters[key]
        if (val !== undefined && val !== '' && val !== null && !(Array.isArray(val) && val.length === 0)) {
          dynamicFilters[key] = Array.isArray(val) ? val.join(',') : val
        }
      })
      if (Object.keys(dynamicFilters).length > 0) {
        params.filters = JSON.stringify(dynamicFilters)
      }
      this.params = params
      // Se o filtro deixou de ser "Vazio", encerra o modo de atribuição em massa
      if (this.params.group_id !== '__none__' && this.selectionMode) {
        this.cancelSelection()
      }
      let loader = this.$loading.show()
      this.getItems({ controlId: this.controlId, params: this.params }).then(() => {
        loader.hide()
      }).catch(error => {
        console.log(error)
        loader.hide()
      })
    },
    doSearch () {
      this.params = { ...this.params, search: this.search }
      this.getReport()
    },
    updateList () {
      this.getReport()
      this.loadGroups()
    },
    async doExport () {
      this.exporting = true
      try {
        const res = await exportItems(this.controlId, this.params)
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'itens_export_' + new Date().toISOString().slice(0, 10) + '.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        successMessage('Exportação concluída!')
      } catch (e) {
        errorMessage('Erro ao exportar.')
      } finally {
        this.exporting = false
      }
    },
    // ── Atribuição de grupo em massa (só no filtro "Vazio") ──
    toggleSelectionMode () {
      this.selectionMode = !this.selectionMode
      if (!this.selectionMode) {
        this.selectedItems = []
        this.assignGroupId = ''
      }
    },
    cancelSelection () {
      this.selectionMode = false
      this.selectedItems = []
      this.assignGroupId = ''
    },
    toggleSelection (id) {
      const i = this.selectedItems.indexOf(id)
      if (i > -1) {
        this.selectedItems.splice(i, 1)
      } else {
        this.selectedItems.push(id)
      }
    },
    toggleSelectAll () {
      if (this.selectedItems.length === this.itemsList.length) {
        this.selectedItems = []
      } else {
        this.selectedItems = this.itemsList.map(it => it.id)
      }
    },
    isSelected (id) {
      return this.selectedItems.includes(id)
    },
    async bulkAssign (allFiltered) {
      if (!this.assignGroupId) {
        errorMessage('Selecione um grupo.')
        return
      }
      const group = this.groupsList.find(g => String(g.id) === String(this.assignGroupId))
      const groupName = group ? group.name : ''
      const count = allFiltered ? this.rows : this.selectedItems.length
      if (count === 0) {
        errorMessage('Nenhum item para atribuir.')
        return
      }
      const result = await Swal.fire({
        title: 'Atribuir grupo?',
        html: `<strong>${count}</strong> item(ns) serão atribuídos ao grupo <strong>${groupName}</strong>.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#556ee6',
        cancelButtonColor: '#74788d',
        confirmButtonText: 'Atribuir',
        cancelButtonText: 'Cancelar'
      })
      if (!result.value) return

      const payload = { group_id: this.assignGroupId }
      if (allFiltered) {
        payload.all_filtered = true
        payload.params = this.params
      } else {
        payload.ids = this.selectedItems
      }

      this.assigning = true
      try {
        const res = await bulkAssignItemsGroup(this.controlId, payload)
        const updated = (res.data && res.data.updated) || count
        successMessage(`${updated} item(ns) atribuído(s) ao grupo ${groupName}.`)
        this.cancelSelection()
        this.updateList()
      } catch (e) {
        errorMessage((e.response && e.response.data && e.response.data.message) || 'Erro ao atribuir grupo.')
      } finally {
        this.assigning = false
      }
    }
  }
}
</script>
<style scoped>
.link { cursor: pointer; }
.swal2-popup { font-size: 11px !important; }
.table-responsive { min-height: 300px; }
.float-sm-end { float: right; }
.copy-icon { color: #adb5bd; cursor: pointer; font-size: 14px; }
.copy-icon:hover { color: #556ee6; }
</style>
